import { useMutation, useQueryClient } from '@tanstack/react-query';
import { COMPLETE_INTEGRATION_CONNECTION } from '../documents';
import {
  CompleteIntegrationConnectionInput,
  IntegrationConnection,
} from '../types';
import { integrationConnectionsCacheKey } from './cacheKeys';
import {
  integrationAuthError,
  useIntegrationAuthFetcher,
} from './useIntegrationAuthFetcher';

interface CompleteResponse {
  integrationAuth: { completeConnection: IntegrationConnection } | null;
}

// Keep code/state in the caller's transient memory, outside mutation variables
// (which React Query retains and can dehydrate for persistence or diagnostics).
export const useCompleteIntegrationConnection = (
  input: CompleteIntegrationConnectionInput | null
) => {
  const queryClient = useQueryClient();
  const fetcher = useIntegrationAuthFetcher<
    CompleteResponse,
    CompleteIntegrationConnectionInput
  >(COMPLETE_INTEGRATION_CONNECTION);
  return useMutation({
    retry: false,
    gcTime: 0,
    mutationFn: async (): Promise<IntegrationConnection> => {
      if (!input?.code || !input.state) throw integrationAuthError();
      const data = await fetcher(input);
      if (!data.integrationAuth) throw integrationAuthError();
      return data.integrationAuth.completeConnection;
    },
    onSuccess: async (connection: IntegrationConnection): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: integrationConnectionsCacheKey(connection.workspaceId),
      });
    },
  });
};
